import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

const ToastContext = createContext(null)

// App-wide toast channel. One provider at the root renders the toast; pages
// consume show/dismiss via useToast(). The auto-dismiss timer is kept in a ref
// so a newer toast clears the older one's timer (no early dismissal) and the
// timer is cleaned up on unmount.
export function ToastProvider({ children }) {
  const [toast, setToast] = useState(null)
  const timerRef = useRef(null)

  const dismiss = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    setToast(null)
  }, [])

  const show = useCallback((message, type = 'success') => {
    if (timerRef.current) clearTimeout(timerRef.current)
    setToast({ message, type })
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      setToast(null)
    }, 4000)
  }, [])

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current)
  }, [])

  const value = useMemo(() => ({ show, dismiss }), [show, dismiss])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toast toast={toast} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within a ToastProvider')
  return ctx
}

export function Toast({ toast, onDismiss }) {
  if (!toast) return null
  const alertClass = {
    success: 'alert-success',
    error: 'alert-error',
    info: 'alert-info',
  }[toast.type] ?? 'alert-info'

  return (
    <div className="toast toast-top toast-end z-50">
      <div
        role={toast.type === 'error' ? 'alert' : 'status'}
        className={`alert ${alertClass} shadow-lg`}
      >
        <span>{toast.message}</span>
        <button className="btn btn-xs btn-ghost" aria-label="Dismiss" onClick={onDismiss}>✕</button>
      </div>
    </div>
  )
}
